/**
 * The search in the documents of the cadastre (app/UI/Modules/Cadastre/templates/CadastreLookup/default.latte).
 * The search form has the number of the proceeding in its parts; the whole number pasted or written into any of them
 * is split into all of them. The list of the found documents counts the chosen ones for the order button.
 * The cadastre takes a while, so the sent search tells so.
 */
export function registerDocumentLookup(snippet: HTMLElement): void {
    snippet.querySelectorAll<HTMLFormElement>('form[data-document-lookup-search]').forEach(form => {
        if (form.dataset.documentLookupInitialized === '1') {
            return;
        }
        form.dataset.documentLookupInitialized = '1';

        const number = form.querySelector<HTMLElement>('[data-document-lookup-number]');

        if (number !== null) {
            number.addEventListener('paste', event => {
                if (splitProceedingNumber(form, event.clipboardData?.getData('text') ?? '')) {
                    event.preventDefault();
                }
            });
            // a number written by hand is split once it is written, not while the office is still being typed
            number.addEventListener('change', event => {
                const field = event.target as HTMLInputElement|HTMLSelectElement;
                field.classList.remove('is-invalid');
                splitProceedingNumber(form, field.value);
            });
        }

        showProgressOnSubmit(form);
    });

    snippet.querySelectorAll<HTMLFormElement>('form[data-document-lookup-order]').forEach(form => {
        const all = form.querySelector<HTMLInputElement>('[data-document-lookup-all]');
        const items = Array.from(form.querySelectorAll<HTMLInputElement>('[data-document-lookup-item]'));

        all?.addEventListener('change', () => {
            items.forEach(item => item.checked = all.checked);
            updateCount(form, items, all);
        });
        items.forEach(item => item.addEventListener('change', () => updateCount(form, items, all)));
        updateCount(form, items, all);
    });
}

/**
 * V-1335/2016-306 as on the page and on the documents; spaces and lower case of a pasted number are let through.
 */
function splitProceedingNumber(form: HTMLFormElement, text: string): boolean {
    const parts = text.trim().toUpperCase().replace(/\s+/g, '')
        .match(/^(V|Z|PGP|PD|ZPV)-?(\d+)\/(\d{4})-(\d+)$/);

    if (parts === null) {
        return false;
    }

    const field = <T extends HTMLElement>(name: string): T|null => form.querySelector<T>(`[name="${name}"]`);
    const type = field<HTMLSelectElement>('type');
    const number = field<HTMLInputElement>('number');
    const year = field<HTMLInputElement>('year');
    const office = field<HTMLSelectElement>('officeId');
    const hint = form.querySelector<HTMLElement>('[data-document-lookup-hint]');

    if (type === null || number === null || year === null || office === null) {
        return false;
    }

    type.value = parts[1];
    number.value = parts[2];
    year.value = parts[3];

    const knownOffice = Array.from(office.options).some(option => option.value === parts[4]);
    office.value = knownOffice ? parts[4] : '';
    office.classList.toggle('is-invalid', ! knownOffice);

    if (hint !== null) {
        hint.classList.toggle('text-danger', ! knownOffice);
        hint.textContent = knownOffice
            ? `Číslo ${parts[1]}-${parts[2]}/${parts[3]}-${parts[4]} jsme rozdělili do polí.`
            : `Pracoviště ${parts[4]} v katastru není, vyberte ho prosím.`;
    }

    return true;
}

function updateCount(form: HTMLFormElement, items: HTMLInputElement[], all: HTMLInputElement|null): void {
    const count = items.filter(item => item.checked).length;

    form.querySelectorAll<HTMLElement>('[data-document-lookup-count]').forEach(element => {
        element.textContent = String(count);
    });
    form.querySelectorAll<HTMLButtonElement>('[data-document-lookup-submit]').forEach(button => {
        button.disabled = count === 0;
    });

    if (all !== null) {
        all.checked = items.length > 0 && count === items.length;
        all.indeterminate = count > 0 && count < items.length;
        all.disabled = items.length === 0;
    }
}

function showProgressOnSubmit(form: HTMLFormElement): void {
    const button = form.querySelector<HTMLButtonElement>('[data-document-lookup-submit]');

    if (button === null) {
        return;
    }

    const caption = button.innerHTML;

    form.addEventListener('submit', event => {
        // after the event is over: the validation of nette forms may have stopped an invalid form, and a button
        // disabled sooner would not be sent with the form
        window.setTimeout(() => {
            if (event.defaultPrevented) {
                return;
            }

            button.disabled = true;
            button.innerHTML = '<span class="spinner-border spinner-border-sm me-1" role="status"></span> Hledáme…';
        });
    });

    // the back button of the browser brings the page back as it was left, with the search still running
    window.addEventListener('pageshow', event => {
        if (event.persisted) {
            button.disabled = false;
            button.innerHTML = caption;
        }
    });
}
